import React from "react";
import {
  Check,
  Loader2,
  Phone,
  RefreshCw,
  Smartphone,
  X,
} from "lucide-react";

/**
 * OtpModal — Twilio Verify identity check shown right before the lead is
 * created. Pure UI: all state and side-effects live in the parent.
 *
 * Props:
 *   phoneDigits   — raw 10-digit string (used to display masked phone).
 *   otpCode       — current code entered by the customer.
 *   setOtpCode    — setter for the code.
 *   otpStep       — idle | sending | sent | verifying | verified.
 *   otpError      — error message to render below the input.
 *   otpCooldown   — seconds remaining before SMS/voice can be re-triggered.
 *   submitting    — true while the lead POST is in flight.
 *   onClose       — close handler.
 *   onResendSms   — resend via SMS handler.
 *   onResendCall  — request voice call handler.
 *   onVerify      — verify code + create lead handler.
 */
export default function OtpModal({
  phoneDigits,
  otpCode,
  setOtpCode,
  otpStep,
  otpError,
  otpCooldown,
  submitting,
  onClose,
  onResendSms,
  onResendCall,
  onVerify,
}) {
  const maskedPhone = phoneDigits.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={() => !submitting && onClose()}
      data-testid="enroll-otp-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[460px] bg-white rounded-2xl shadow-2xl p-6 md:p-7"
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <span className="grid place-items-center w-12 h-12 rounded-2xl bg-velcor-green/15 text-velcor-green ring-1 ring-velcor-green/30 mb-3">
              <Smartphone className="w-5 h-5" strokeWidth={2.4} />
            </span>
            <h3
              className="display text-velcor-ink text-[22px] leading-tight"
              style={{ fontWeight: 600, letterSpacing: "-0.018em" }}
            >
              Verifiquemos tu identidad
            </h3>
            <p className="text-slate-500 text-[12.5px] mt-1 max-w-[330px] leading-relaxed">
              Enviamos un código de 6 dígitos a{" "}
              <strong className="font-mono text-velcor-ink">{maskedPhone}</strong>.
            </p>
          </div>
          <button
            type="button"
            onClick={() => !submitting && onClose()}
            disabled={submitting}
            className="grid place-items-center w-9 h-9 rounded-full hover:bg-slate-100 disabled:opacity-30"
            aria-label="Cerrar"
            data-testid="enroll-otp-modal-close"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div>
          <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 mb-2">
            Código de verificación <span className="text-rose-500">*</span>
          </label>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D+/g, "").slice(0, 8))}
            placeholder="123456"
            autoFocus
            className="w-full h-13 px-4 py-3 rounded-xl border border-slate-300 bg-white text-[20px] font-mono tracking-[0.4em] text-center text-velcor-ink focus:outline-none focus:border-velcor-green focus:ring-2 focus:ring-velcor-green/15"
            data-testid="enroll-otp-modal-input"
          />
          {otpError && (
            <div
              className="mt-2 text-[12.5px] font-bold text-rose-600"
              data-testid="enroll-otp-modal-error"
            >
              {otpError}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 mt-5 pt-4 border-t border-slate-100">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={onResendSms}
              disabled={submitting || otpStep === "sending" || otpCooldown > 0}
              className="text-[12px] font-bold text-velcor-green hover:underline disabled:opacity-50 inline-flex items-center gap-1"
              data-testid="enroll-otp-modal-resend"
            >
              <RefreshCw className="w-3 h-3" strokeWidth={2.4} />
              {otpCooldown > 0 ? `Reenviar en ${otpCooldown}s` : "Reenviar SMS"}
            </button>
            <button
              type="button"
              onClick={onVerify}
              disabled={otpCode.length < 4 || submitting}
              className="btn-green h-11 px-5 text-[13px] inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="enroll-otp-modal-verify"
            >
              {submitting || otpStep === "verifying" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              {submitting ? "Activando…" : "Verificar y confirmar"}
            </button>
          </div>

          {/* Voice fallback — for customers who don't receive SMS (most often
              landlines, VoIP, MVNO carriers or weak signal). */}
          <button
            type="button"
            onClick={onResendCall}
            disabled={submitting || otpStep === "sending" || otpCooldown > 0}
            className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-full border-2 border-velcor-green/40 text-velcor-green hover:bg-velcor-green/5 disabled:opacity-50 font-bold text-[12.5px] transition-colors"
            data-testid="enroll-otp-modal-call"
          >
            <Phone className="w-3.5 h-3.5" strokeWidth={2.4} />
            {otpCooldown > 0
              ? `Llamarme con el código (${otpCooldown}s)`
              : "Llamarme con el código"}
          </button>
          <p className="text-center text-[10.5px] text-slate-400 leading-snug -mt-1">
            ¿No recibiste el SMS? Te llamamos y un sistema automatizado te dictará el código.
          </p>
        </div>
      </div>
    </div>
  );
}
